import { useQuery } from '@tanstack/react-query';
import axios from 'axios';

import settings from '@/constants/constants';
import { Logger } from '@/utils/logger';
import type { DELIVERY_OPTION, Price } from '@montugroup/circuit-shared';

type Product = {
  name: string | null;
  short_name: string | null;
  image_url: string | null;
  price: Price | null;
  is_out_of_stock: boolean | null;
  reasoning_toggle: boolean | null;
  formulation_id: number | null;
  ProductImages: Array<unknown>; // todo
  ProductFormulation: {
    id: number | null;
    name: string | null;
    whitelabel_image_url: string | null;
  };
};

export type OrderDetailsProducts = {
  quantity: number | null;
  product_id: number | null;
  price: string | null;
  id: number | null;
  Product: Product;
};

export type OrderPharmacyDetails = {
  id: number;
  name: string;
  address1: string;
  address2: string | null;
  city: string;
  state: string;
  zipCode: string;
};

export type OrderDetails = {
  order_code: string;
  order_date: string;
  id: number;
  total_price: string | null;
  shipment_carrier: string | null;
  shipment_tracking_id: string | null;
  order_status_id: number;
  patient_id: number | null;
  delivered: boolean;
  delivered_date: string | null;
  shipped_date: string | null;
  dispensed_date: string | null;
  Patient: {
    id: number;
    is_discharge: string | null;
  };
  Pharmacy?: OrderPharmacyDetails;
  OrderProducts: OrderDetailsProducts[];
  OrderStatus: {
    name: string | null;
  };
  OrderManagementShopify: string | null;
  DeliveryOption?: {
    id: DELIVERY_OPTION;
    name: keyof typeof DELIVERY_OPTION;
  };
};

const logger = new Logger('useGetOrderDetails');

export default function useGetOrderDetails(orderCode: string, enabled: boolean) {
  const { data, isError, isLoading } = useQuery({
    queryKey: ['orderDetails', orderCode],
    queryFn: async () => {
      try {
        const res = await axios.get<OrderDetails[]>(`${settings.url}/patient/orderDetail/${orderCode}`);
        return res.data;
      } catch (error) {
        logger.error(error);
        throw new Error('Failed to fetch order details');
      }
    },
    enabled,
  });

  return {
    data,
    isError,
    isLoading,
  };
}
